import { AssistantRuntimeProvider, useLocalRuntime } from "@assistant-ui/react";
import { Thread } from "@/components/assistant-ui/thread";
import "@/components/assistant-ui/assistant-ui.css";
import { useAuthContext } from "@/contexts/AuthContext";
import { createStationCopyEngineAdapter } from "@/lib/copy-chat-adapter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Check, ArrowLeft } from "lucide-react";
import type { Station } from "@shared/schema";

/**
 * Back-and-forth refinement chat for the workbench Copy tool. Same runtime,
 * thread surface, and chat endpoint as the classic Copy Engine (station
 * prompt + copyChatLimiter apply); only the chrome is workbench-styled. The
 * initial generated copy seeds the thread as the first assistant message.
 * After each completed turn the full transcript is saved onto the
 * generation's copy_messages row (chat_history), so the whole session shows
 * up in the sponsor's history below the chat. Saving is best-effort and
 * never interrupts the chat.
 */
export default function CopyChatPanel({
  station, initialMessage, formData, copyMessageId, onNewCopy,
}: {
  station: Station;
  initialMessage: string;
  formData: Record<string, unknown>;
  copyMessageId: string | null;
  onNewCopy: () => void;
}) {
  const { getAuthToken } = useAuthContext();
  const sponsorId = typeof formData?.sponsorId === "string" ? formData.sponsorId : null;

  const adapter = createStationCopyEngineAdapter(station, formData, getAuthToken, (transcript) => {
    if (!copyMessageId) return;
    apiRequest(`/api/copy-messages/${copyMessageId}/chat`, "PUT", { messages: transcript })
      .then(() => {
        if (sponsorId) {
          queryClient.invalidateQueries({ queryKey: [`/api/sponsors/${sponsorId}/activity`] });
        }
      })
      .catch(() => {
        // Best-effort persistence; the visible chat is unaffected.
      });
  });
  const runtime = useLocalRuntime(adapter, {
    initialMessages: [
      {
        id: "initial",
        role: "assistant" as const,
        content: [{ type: "text" as const, text: initialMessage }],
      },
    ],
  });

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <p className="text-sm font-medium text-green-700 flex items-center gap-1.5">
          <Check className="h-4 w-4" /> Copy generated. Chat below to refine it - the full chat is saved to this sponsor's history.
        </p>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="rounded-full" onClick={onNewCopy} data-testid="button-new-copy-chat">
            <ArrowLeft className="h-4 w-4 mr-1" /> New copy
          </Button>
        </div>
      </div>
      <div className="rounded-lg border border-emerald-100 bg-white overflow-hidden h-[540px] flex flex-col">
        <AssistantRuntimeProvider runtime={runtime}>
          <Thread />
        </AssistantRuntimeProvider>
      </div>
    </div>
  );
}
